import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Zap, Copy, Check, Save, Loader2, Pin, ChevronRight, Gift, FileText, Download, Wand2, Sparkles } from "lucide-react";
import { generateScript, saveScript, generateLeadMagnet, classifyContent, CATEGORIES, TIPI } from "@/lib/api";
import { exportMagnetPdf } from "@/lib/pdf";
import { TipoKpi } from "@/components/Ideas";
import { toast } from "sonner";

const CATEGORY_HINT = {
  Connessione: "Scena quotidiana che dà speranza/agency sullo sfondo della fatica economica. Rara, mai psicologia pura.",
  "Tabù di coppia": "SOLO come conseguenza dell'economia: soldi nascosti, lavoro che vi svuota, distanza da stanchezza. Mai sesso/rituali/psicologia.",
  Sistema: "Il CUORE: lavoro, precarietà, caro-vita, mutui, tasse, figli-costo, gender pay gap che schiaccia la coppia.",
};

const TIPO_HINT = {
  Attenzione: "Frame 01/02: fatto + scena di casa → paradosso → concessione → tesi → punchline. Nessuna CTA.",
  Educazione: "Frame 01/02 con il «cosa significa per te» concreto. Nessuna CTA, chiude sulla punchline.",
  Autorevolezza: "Caso reale + risposta concreta coi numeri. Nessuna CTA.",
  Conversione: "Frame 03: valore vero (3 mosse) + UNA sola CTA «Scrivimi INIZIAMO». Mai il workshop.",
};

function CopyBtn({ text, id, testid }) {
  const [copied, setCopied] = useState(false);
  const onCopy = () => {
    navigator.clipboard.writeText(text || "");
    setCopied(true);
    toast.success("Copiato");
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <button
      data-testid={testid}
      onClick={onCopy}
      className="text-neutral-500 hover:text-[#D92D20] transition-colors"
      title="Copia"
    >
      {copied ? <Check size={16} /> : <Copy size={16} />}
    </button>
  );
}

function Section({ label, value, testid, accent }) {
  if (!value) return null;
  return (
    <div
      className="border-l-2 pl-4 py-1 group"
      style={{ borderColor: accent ? "#D92D20" : "#333" }}
    >
      <div className="flex items-center justify-between mb-1">
        <span className="label-caps" style={{ color: accent ? "#D92D20" : "#9CA3AF" }}>
          {label}
        </span>
        <CopyBtn text={value} testid={`copy-${testid}`} />
      </div>
      <p className="text-neutral-200 leading-relaxed whitespace-pre-wrap" data-testid={`out-${testid}`}>
        {value}
      </p>
    </div>
  );
}

export default function Generator({ prefill }) {
  const [categoria, setCategoria] = useState("Tabù di coppia");
  const [tema, setTema] = useState("");
  const [tipo, setTipo] = useState("Attenzione");
  const [protagonista, setProtagonista] = useState("");
  const [note, setNote] = useState("");
  const [tiHanno, setTiHanno] = useState(false);
  const [includiObiezione, setIncludiObiezione] = useState(true);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null);
  const [magnet, setMagnet] = useState(null);
  const [magnetLoading, setMagnetLoading] = useState(false);
  const [classifying, setClassifying] = useState(false);
  const [classifyInfo, setClassifyInfo] = useState(null);

  useEffect(() => {
    if (prefill) {
      if (prefill.categoria && CATEGORIES.includes(prefill.categoria)) setCategoria(prefill.categoria);
      if (prefill.tipo && TIPI.includes(prefill.tipo)) setTipo(prefill.tipo);
      setTema(prefill.angolo || prefill.hook_fissato || "");
      setProtagonista(prefill.protagonista || "");
      setTiHanno(prefill.categoria === "Sistema");
      setResult(null);
    }
  }, [prefill]);

  const onClassify = async () => {
    if (!tema.trim()) {
      toast.error("Incolla prima l'idea o la bozza nel campo Tema / Angolo");
      return;
    }
    setClassifying(true);
    setClassifyInfo(null);
    try {
      const data = await classifyContent(tema);
      if (data.categoria && CATEGORIES.includes(data.categoria)) setCategoria(data.categoria);
      if (data.tipo && TIPI.includes(data.tipo)) setTipo(data.tipo);
      if (data.protagonista) setProtagonista(data.protagonista);
      setTiHanno(!!data.usa_ti_hanno_fatto_credere);
      setClassifyInfo(data);
      toast.success("Ecco il mio consiglio 👇");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore nella classificazione");
    } finally {
      setClassifying(false);
    }
  };

  const onGenerate = async () => {
    if (!tema.trim()) {
      toast.error("Scrivi un tema o un angolo");
      return;
    }
    setLoading(true);
    setResult(null);
    setMagnet(null);
    try {
      const data = await generateScript({
        categoria,
        tipo,
        tema,
        protagonista,
        note,
        usa_ti_hanno_fatto_credere: tiHanno,
        includi_obiezione: includiObiezione,
      });
      setResult(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore nella generazione");
    } finally {
      setLoading(false);
    }
  };

  const onGenerateMagnet = async () => {
    if (!result) return;
    setMagnetLoading(true);
    try {
      const data = await generateLeadMagnet({
        tema: result.tema || tema,
        categoria: result.categoria || categoria,
        risorsa: result.risorsa || "",
      });
      setMagnet(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore nella creazione della risorsa");
    } finally {
      setMagnetLoading(false);
    }
  };

  const onSave = async () => {
    if (!result) return;
    setSaving(true);
    try {
      await saveScript({ ...result, lead_magnet: magnet || null });
      toast.success("Salvato nell'archivio");
    } catch (e) {
      toast.error("Errore nel salvataggio");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,380px)_1fr] gap-6">
      {/* Left: controls */}
      <div className="bg-[#121212] border border-[#262626] rounded-md p-6 h-fit lg:sticky lg:top-6">
        <h2 className="font-display font-extrabold text-2xl mb-6 tracking-tight">Nuovo Script</h2>

        <label className="label-caps block mb-2">Categoria</label>
        <div className="grid grid-cols-3 gap-2 mb-1">
          {CATEGORIES.map((c) => (
            <button
              key={c}
              data-testid={`cat-${c}`}
              onClick={() => setCategoria(c)}
              className={`text-xs font-semibold py-2 px-1 rounded-sm border transition-colors ${
                categoria === c
                  ? "bg-[#D92D20] border-[#D92D20] text-white"
                  : "bg-transparent border-[#333] text-neutral-400 hover:border-neutral-500"
              }`}
            >
              {c}
            </button>
          ))}
        </div>
        <p className="text-xs text-neutral-500 mb-5 min-h-[32px]">{CATEGORY_HINT[categoria]}</p>

        <label className="label-caps block mb-2">Tipo di contenuto</label>
        <div className="grid grid-cols-2 gap-2 mb-1">
          {TIPI.map((t) => (
            <button
              key={t}
              data-testid={`tipo-${t}`}
              onClick={() => setTipo(t)}
              className={`text-xs font-semibold py-2 px-1 rounded-sm border transition-colors ${
                tipo === t
                  ? "bg-white border-white text-black"
                  : "bg-transparent border-[#333] text-neutral-400 hover:border-neutral-500"
              }`}
            >
              {t}
            </button>
          ))}
        </div>
        <p className="text-xs text-neutral-500 mb-5 min-h-[32px]">{TIPO_HINT[tipo]}</p>

        <label className="label-caps block mb-2">Tema / Angolo</label>
        <textarea
          data-testid="input-tema"
          value={tema}
          onChange={(e) => setTema(e.target.value)}
          rows={3}
          placeholder="Es. La donna che guadagna più del partner si sente in difetto"
          className="w-full bg-[#0A0A0A] border border-[#262626] rounded-sm p-3 text-sm text-neutral-100 focus:outline-none focus:border-[#D92D20] mb-3 resize-none"
        />

        <button
          data-testid="classify-btn"
          onClick={onClassify}
          disabled={classifying}
          className="w-full mb-3 border border-[#333] hover:border-neutral-500 disabled:opacity-60 text-neutral-300 font-semibold py-2.5 rounded-sm flex items-center justify-center gap-2 transition-colors text-sm"
          title="Non sai categoria e tipo? Incolla l'idea qui sopra e lascia che decida io."
        >
          {classifying ? <Loader2 className="animate-spin" size={16} /> : <Wand2 size={16} />}
          {classifying ? "Analizzo..." : "Aiutami a classificare"}
        </button>

        {classifyInfo && (
          <motion.div
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            data-testid="classify-result"
            className="mb-4 bg-[#0A0A0A] border border-[#262626] rounded-sm p-3"
          >
            <div className="flex items-center gap-2 mb-2">
              <Sparkles size={13} className="text-[#D92D20]" />
              <span className="label-caps text-[#D92D20]">Consiglio applicato</span>
            </div>
            <div className="flex flex-wrap gap-2 mb-2">
              <span className="text-xs font-semibold bg-[#D92D20] text-white px-2 py-1 rounded-sm">{classifyInfo.categoria}</span>
              <span className="text-xs font-semibold bg-white text-black px-2 py-1 rounded-sm">{classifyInfo.tipo}</span>
              {classifyInfo.usa_ti_hanno_fatto_credere && (
                <span className="text-xs font-semibold border border-[#333] text-neutral-300 px-2 py-1 rounded-sm">Formula "Ti hanno fatto credere"</span>
              )}
            </div>
            {classifyInfo.motivazione && (
              <p className="text-xs text-neutral-400 leading-relaxed" data-testid="classify-motivazione">{classifyInfo.motivazione}</p>
            )}
            <p className="text-[11px] text-neutral-600 mt-2">Ho già impostato i menu qui sopra: puoi correggerli a mano se non sei d'accordo.</p>
          </motion.div>
        )}

        <label className="label-caps block mb-2">Protagonista da interpellare</label>
        <input
          data-testid="input-protagonista"
          value={protagonista}
          onChange={(e) => setProtagonista(e.target.value)}
          placeholder="Es. la moglie stanca, il padre assente"
          className="w-full bg-[#0A0A0A] border border-[#262626] rounded-sm p-3 text-sm text-neutral-100 focus:outline-none focus:border-[#D92D20] mb-4"
        />

        <label className="label-caps block mb-2">Note aggiuntive</label>
        <input
          data-testid="input-note"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Dettagli, contesto, tono..."
          className="w-full bg-[#0A0A0A] border border-[#262626] rounded-sm p-3 text-sm text-neutral-100 focus:outline-none focus:border-[#D92D20] mb-4"
        />

        <label
          className={`flex items-start gap-3 mb-6 cursor-pointer p-3 rounded-sm border transition-colors ${
            tiHanno ? "border-[#D92D20] bg-[#D92D20]/5" : "border-[#262626]"
          }`}
        >
          <input
            data-testid="toggle-formula"
            type="checkbox"
            checked={tiHanno}
            onChange={(e) => setTiHanno(e.target.checked)}
            className="mt-1 accent-[#D92D20]"
          />
          <span className="text-sm text-neutral-300">
            <span className="font-semibold text-neutral-100">Formula "Ti hanno fatto credere"</span>
            <br />
            <span className="text-xs text-neutral-500">Svolta con capovolgimento sul sistema (lavoro/mutuo/figli)</span>
          </span>
        </label>

        <label
          className={`flex items-start gap-3 mb-6 cursor-pointer p-3 rounded-sm border transition-colors ${
            includiObiezione ? "border-[#D92D20] bg-[#D92D20]/5" : "border-[#262626]"
          }`}
        >
          <input
            data-testid="toggle-obiezione"
            type="checkbox"
            checked={includiObiezione}
            onChange={(e) => setIncludiObiezione(e.target.checked)}
            className="mt-1 accent-[#D92D20]"
          />
          <span className="text-sm text-neutral-300">
            <span className="font-semibold text-neutral-100">Concessione articolata</span>
            <br />
            <span className="text-xs text-neutral-500">
              {includiObiezione
                ? "«È vero: … Però …» in due frasi: lo scudo anti-rissa, mai difensivo."
                : "Minima: una sola frase «È vero che… però…», poi dritto a tesi e punchline."}
            </span>
          </span>
        </label>

        <TipoKpi tipo={tipo} className="mb-3" />

        <button
          data-testid="generate-btn"
          onClick={onGenerate}
          disabled={loading}
          className="w-full bg-[#D92D20] hover:bg-[#B42318] disabled:opacity-60 text-white font-bold py-3.5 rounded-sm flex items-center justify-center gap-2 transition-colors uppercase tracking-wider text-sm"
        >
          {loading ? <Loader2 className="animate-spin" size={18} /> : <Zap size={18} />}
          {loading ? "Genero..." : "Genera Script"}
        </button>
      </div>

      {/* Right: output */}
      <div>
        {!result && !loading && (
          <div className="border border-dashed border-[#262626] rounded-md h-full min-h-[400px] flex flex-col items-center justify-center text-center p-10">
            <Pin size={40} className="text-neutral-700 mb-4" />
            <p className="text-neutral-500 max-w-sm">
              Scegli categoria e tema, poi genera. Lo script apparirà qui con hook fissato,
              struttura completa e caption pronti da usare.
            </p>
          </div>
        )}

        {loading && (
          <div className="border border-[#262626] rounded-md h-full min-h-[400px] flex flex-col items-center justify-center">
            <Loader2 className="animate-spin text-[#D92D20] mb-4" size={32} />
            <p className="text-neutral-400">Massimiliano sta scrivendo...</p>
          </div>
        )}

        {result && (
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-5"
            data-testid="script-result"
          >
            {/* Hook fissato */}
            <div className="bg-[#121212] border border-[#D92D20] rounded-md p-6 relative">
              <div className="flex items-center justify-between mb-2">
                <span className="label-caps text-[#D92D20] flex items-center gap-2">
                  <Pin size={13} /> Hook Fissato
                </span>
                <CopyBtn text={result.hook_fissato} testid="copy-hookfissato" />
              </div>
              <p
                className="font-display font-extrabold text-2xl leading-tight tracking-tight text-white"
                data-testid="out-hookfissato"
              >
                {result.hook_fissato}
              </p>
            </div>

            {/* Structured sections */}
            <div className="bg-[#121212] border border-[#262626] rounded-md p-6 space-y-5">
              {result.frame && (
                <span className="text-[0.62rem] uppercase tracking-wider px-2 py-0.5 rounded-sm bg-[#1a1a1a] border border-[#262626] text-neutral-300" data-testid="out-frame">
                  Frame {result.frame}{result.frame === "03" ? " · CTA INIZIAMO" : " · senza CTA"}
                </span>
              )}
              <Section label="Notizia nuda · prima riga" value={result.hook} testid="hook" />
              <Section label="Fatto + scena di casa" value={result.story} testid="story" />
              <Section label="Paradosso + tesi" value={result.svolta} testid="svolta" accent />
              <Section label="Concessione «È vero… però»" value={result.obiezione} testid="obiezione" />
              <Section label="Punchline" value={result.punchline} testid="punchline" accent />
              <Section label="Frase inoltrabile" value={result.frase_inoltrabile} testid="frase-inoltrabile" />
              <Section label="CTA (solo frame 03)" value={result.cta} testid="cta" />
            </div>

            {/* Copione completo */}
            {result.copione_completo && (
              <div className="bg-[#121212] border border-[#262626] rounded-md p-6">
                <div className="flex items-center justify-between mb-2">
                  <span className="label-caps flex items-center gap-2">
                    <ChevronRight size={13} /> Copione completo
                  </span>
                  <CopyBtn text={result.copione_completo} testid="copy-copione" />
                </div>
                <p className="text-neutral-200 leading-relaxed whitespace-pre-wrap" data-testid="out-copione">
                  {result.copione_completo}
                </p>
              </div>
            )}

            {/* Risorsa conversione */}
            {(result.risorsa || result.parola_chiave) && (
              <div className="bg-[#0e1a12] border border-[#16A34A]/50 rounded-md p-6" data-testid="conversion-box">
                <span className="label-caps text-[#22C55E] flex items-center gap-2 mb-3">
                  <Gift size={14} /> Conversione · risorsa gratuita
                </span>
                {result.risorsa && (
                  <p className="text-neutral-200 mb-3"><span className="text-neutral-500">Risorsa:</span> {result.risorsa}</p>
                )}
                {result.parola_chiave && (
                  <div className="flex items-center gap-3">
                    <span className="text-neutral-500 text-sm">Parola chiave DM:</span>
                    <span className="font-display font-extrabold text-lg text-white bg-[#16A34A]/15 border border-[#16A34A]/40 rounded-sm px-3 py-1" data-testid="out-parolachiave">
                      {result.parola_chiave}
                    </span>
                    <CopyBtn text={result.parola_chiave} testid="copy-parolachiave" />
                  </div>
                )}

                {!magnet && (
                  <button
                    data-testid="generate-magnet-btn"
                    onClick={onGenerateMagnet}
                    disabled={magnetLoading}
                    className="mt-4 bg-[#16A34A] hover:bg-[#15803D] disabled:opacity-60 text-white font-bold py-2.5 px-5 rounded-sm flex items-center gap-2 transition-colors text-sm uppercase tracking-wider"
                  >
                    {magnetLoading ? <Loader2 className="animate-spin" size={16} /> : <FileText size={16} />}
                    {magnetLoading ? "Creo la risorsa..." : "Crea la risorsa gratuita"}
                  </button>
                )}

                {magnet && (
                  <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="mt-5 border-t border-[#16A34A]/30 pt-5 space-y-4" data-testid="magnet-result">
                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <span className="label-caps text-[#22C55E]">Risorsa gratuita pronta</span>
                        <CopyBtn text={`${magnet.titolo}\n\n${magnet.contenuto}`} testid="copy-magnet" />
                      </div>
                      <p className="font-display font-extrabold text-xl text-white leading-tight">{magnet.titolo}</p>
                      {magnet.formato && (
                        <p className="text-xs text-neutral-500 mt-1">
                          Formato: <span className="text-neutral-300">{magnet.formato}</span>
                          {magnet.perche_questo_formato ? ` · ${magnet.perche_questo_formato}` : ""}
                        </p>
                      )}
                    </div>

                    {magnet.messaggio_dm && (
                      <div className="bg-[#0A0A0A] border border-[#262626] rounded-sm p-4">
                        <div className="flex items-center justify-between mb-1">
                          <span className="label-caps">Messaggio DM di consegna</span>
                          <CopyBtn text={magnet.messaggio_dm} testid="copy-magnet-dm" />
                        </div>
                        <p className="text-neutral-300 leading-relaxed whitespace-pre-wrap">{magnet.messaggio_dm}</p>
                      </div>
                    )}

                    <div className="bg-[#0A0A0A] border border-[#262626] rounded-sm p-4">
                      <div className="flex items-center justify-between mb-1">
                        <span className="label-caps">Contenuto della risorsa</span>
                        <CopyBtn text={magnet.contenuto} testid="copy-magnet-content" />
                      </div>
                      <p className="text-neutral-200 leading-relaxed whitespace-pre-wrap" data-testid="out-magnet-content">{magnet.contenuto}</p>
                    </div>

                    <div className="flex gap-5 items-center">
                    <button
                      data-testid="regenerate-magnet-btn"
                      onClick={onGenerateMagnet}
                      disabled={magnetLoading}
                      className="text-sm font-semibold text-[#22C55E] hover:text-white transition-colors flex items-center gap-1.5 disabled:opacity-60"
                    >
                      {magnetLoading ? <Loader2 className="animate-spin" size={14} /> : <FileText size={14} />}
                      Rigenera la risorsa
                    </button>
                    <button
                      data-testid="export-magnet-pdf-btn"
                      onClick={() => exportMagnetPdf(magnet)}
                      className="text-sm font-semibold text-neutral-300 hover:text-white transition-colors flex items-center gap-1.5"
                    >
                      <Download size={14} /> Esporta PDF
                    </button>
                    </div>
                  </motion.div>
                )}
              </div>
            )}

            {/* Caption + keywords */}
            <div className="bg-[#121212] border border-[#262626] rounded-md p-6">
              <div className="flex items-center justify-between mb-2">
                <span className="label-caps">Caption</span>
                <CopyBtn text={result.caption} testid="copy-caption" />
              </div>
              <p className="text-neutral-300 leading-relaxed whitespace-pre-wrap mb-4">{result.caption}</p>
              <div className="flex flex-wrap gap-2 mb-3">
                {result.keywords?.map((k, i) => (
                  <span key={i} className="text-xs bg-[#1A1A1A] border border-[#262626] text-neutral-400 px-2 py-1 rounded-sm">
                    {k}
                  </span>
                ))}
              </div>
              <div className="flex flex-wrap gap-2">
                {result.hashtags?.map((h, i) => (
                  <span key={i} className="text-xs text-[#D92D20]">{h}</span>
                ))}
              </div>
            </div>

            <div className="flex gap-3">
              <button
                data-testid="save-btn"
                onClick={onSave}
                disabled={saving}
                className="bg-white text-black font-bold py-3 px-6 rounded-sm flex items-center gap-2 hover:bg-neutral-200 transition-colors disabled:opacity-60"
              >
                {saving ? <Loader2 className="animate-spin" size={16} /> : <Save size={16} />}
                Salva in archivio
              </button>
              <button
                data-testid="regenerate-btn"
                onClick={onGenerate}
                className="border border-[#333] text-neutral-300 font-semibold py-3 px-6 rounded-sm hover:border-neutral-500 transition-colors"
              >
                Rigenera
              </button>
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
}
